import { createApp } from 'vue';
import axios from 'axios';
import vue3GoogleLogin from 'vue3-google-login';
import { createPinia } from 'pinia';

import App from './App.vue';
import router from './router.js';
import { refreshUserSessionRequest } from './core/services/apiServices';
import { installSessionAuth } from './core/services/sessionAuth';
import { useNotification } from './core/store/notification.store';
import { getCurrentSeasonPath, readStoredPreferences } from './core/store/preferences.store';
import { useUser } from './core/store/user.store';
import { buildDefaultRouteSeo, setSeoMeta } from './core/seo/seo';
import { initializeGoogleAnalytics, initializeRedditPixel, initializeXPixel, trackGoogleAnalyticsPageView } from './core/services/analytics';
import {
    isAnalyticsSuppressedPath,
    isCurrentAnalyticsSuppressedPath
} from './core/utils/privacyRoute';

// Vuetify
import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import { aliases, mdi } from 'vuetify/iconsets/mdi-svg';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';

// Other
import 'v3-infinite-loading/lib/style.css';

const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID || '767576564295-7hg9ckun915du0fpa3rmu4scbttnnpdd.apps.googleusercontent.com';
const storedPreferences = readStoredPreferences();
const defaultTheme = storedPreferences.theme;
const INITIAL_BOOT_GUARD_TIMEOUT_MS = 5000;

const vuetify = createVuetify({
    components,
    directives,
    theme: {
        defaultTheme,
        themes: {
            light: {
                dark: false,
                colors: {
                    background: '#e5e8ec',
                    surface: '#ffffff',
                    primary: '#4c89ca'
                }
            },
            dark: {
                dark: true,
                colors: {
                    background: '#12161c',
                    surface: '#1a212b',
                    primary: '#7ab1ea'
                }
            }
        }
    },
    icons: {
        defaultSet: 'mdi',
        aliases,
        sets: {
            mdi,
        }
    }
});

const pinia = createPinia();
const userStore = useUser(pinia);
const notificationStore = useNotification(pinia);
userStore.hydrateFromStorage();

const getDefaultSeasonPath = () => getCurrentSeasonPath();

const clearFrontendSession = ({ notify = false } = {}) => {
    const hadSession = userStore.isAuthenticated;
    userStore.clearSession();

    if (notify && hadSession) {
        notificationStore.notify({ message: 'Your session expired please login again.' });
    }

    const currentRoute = router.currentRoute?.value;
    if (currentRoute?.meta?.requiresAuth) {
        router.push(getDefaultSeasonPath());
    }
};

installSessionAuth({
    client: axios,
    userStore,
    refreshSession: refreshUserSessionRequest,
    clearSession: clearFrontendSession
});

const app = createApp(App);

const releaseInitialBootGuard = () => {
    if (typeof document === 'undefined') {
        return;
    }
    document.documentElement.classList.remove('ab-app-booting');
};

router.afterEach((to) => {
    setSeoMeta(buildDefaultRouteSeo(to));
    if (!isAnalyticsSuppressedPath(to.path)) {
        trackGoogleAnalyticsPageView({
            path: to.fullPath,
            title: document.title
        });
    }
});

if (!isCurrentAnalyticsSuppressedPath()) {
    initializeGoogleAnalytics();
    initializeRedditPixel();
    initializeXPixel();
}

app
    .use(pinia)
    .use(router)
    .use(vuetify)
    .use(vue3GoogleLogin, {
        clientId: GOOGLE_CLIENT_ID
    })
    .mount('#app');

router.isReady()
    .then(() => {
        setSeoMeta(buildDefaultRouteSeo(router.currentRoute.value));
    })
    .finally(() => {
        releaseInitialBootGuard();
    });

if (typeof window !== 'undefined') {
    window.setTimeout(() => {
        releaseInitialBootGuard();
    }, INITIAL_BOOT_GUARD_TIMEOUT_MS);
}
